import { useCallback } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { swSettings } from "app/common/services/swSettings";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { useKeywordLists } from "app/modules/demand-analysis/keyword-lists";
import type { IDurationObject } from "app/services/DurationService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";

import { useKeywordListsNodeAPIQueryParams } from "../../hooks/useKeywordListsNodeAPIQueryParams";
import { getSearchTrendsByCountryExcelURL } from "../../utils/getExcelURL";

import { type SearchTrendsByCountryData, searchTrendsByCountryQueryOptions } from "./searchTrendsByCountry";
import { selectSearchTrendsByCountryData } from "./selectors";
import { TrafficByCountryMap } from "./TrafficByCountryMap";
import { TrafficByCountryTable } from "./TrafficByCountryTable";
import { WORLDWIDE_SUBTITLE_FILTER, getTrafficByCountryVariant } from "./utils";

const WidgetContainerWrapper = styled.div`
    width: 100%;
    margin-bottom: 24px;
`;

const ContentWrapper = styled.div<{ stretchContent: boolean }>`
    display: grid;
    grid-template-columns: ${({ stretchContent }) => (stretchContent ? "1fr" : "1fr 1fr")};
    padding: 24px 0;
`;

const getSubtitles = (duration: IDurationObject<string>, webSource: string) => {
    return [
        {
            filter: "date" as const,
            value: {
                from: duration.from,
                to: duration.to,
                compareFrom: duration.compareFrom,
                compareTo: duration.compareTo,
            },
        },
        WORLDWIDE_SUBTITLE_FILTER,
        {
            filter: "webSource" as const,
            value: webSource,
            displayName: translate(`websources.${webSource.toLowerCase()}`),
        },
    ];
};

export const TrafficByCountry = () => {
    const {
        params: { duration, comparedDuration, webSource },
    } = useCurrentStateAndParams();
    const t = useTranslation();
    const { keywordLists, getKeywordListById } = useKeywordLists();

    let durationData = DurationService.getDurationData(duration, comparedDuration).forAPI;
    if (durationData.isWindow) {
        durationData = DurationService.getDurationData(BasicDurations.LAST_MONTH).forAPI;
    }

    const subtitles = getSubtitles(durationData, webSource);

    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const variant = getTrafficByCountryVariant(keywordLists.length, isPeriodOverPeriod);
    const isYearOverYear = comparedDuration === BasicDurations.LAST_TWELVE_MONTHS;
    const isMapDisplayed = variant !== "ComparisonAndPoP";

    const queryOptions = searchTrendsByCountryQueryOptions({
        from: durationData.from,
        to: durationData.to,
        compareFrom: durationData.compareFrom,
        compareTo: durationData.compareTo,
        isWindow: durationData.isWindow,
        keywordListIds: keywordLists.map((keywordList) => keywordList.keywordListId),
    });
    const select = useCallback(
        (data: SearchTrendsByCountryData) => selectSearchTrendsByCountryData(getKeywordListById, data),
        [getKeywordListById],
    );
    const { data, isLoading, isError } = useQuery({ ...queryOptions, select });

    const searchTrendsQueryParams = useKeywordListsNodeAPIQueryParams();
    const excelParams = {
        ...searchTrendsQueryParams,
        from: durationData.from,
        to: durationData.to,
        compareFrom: durationData.compareFrom,
        compareTo: durationData.compareTo,
        isWindow: durationData.isWindow,
    };
    const excelURL = getSearchTrendsByCountryExcelURL(excelParams);

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel/Map");
    };

    return (
        <WidgetContainerWrapper>
            <WidgetContainer
                withBorder
                automationId="search-trends-top-coutries-by-volume-widget"
                noData={isError}
                title={t("demand.analysis.search.trends.map.widget.title")}
                tooltip={t("demand.analysis.search.trends.map.widget.tooltip")}
                subtitleFilters={subtitles}
                isExcelDisabled={!swSettings.current.resources.IsExcelAllowed}
                downloadProps={{ downloadTooltipText: t("demand.analysis.search.trends.map.widget.download.tooltip") }}
                onExcelClick={handleExcelClick}
                excelUrl={excelURL}
            >
                <ContentWrapper stretchContent={!isMapDisplayed}>
                    {isMapDisplayed ? (
                        <TrafficByCountryMap
                            duration={durationData}
                            isPeriodOverPeriod={isPeriodOverPeriod}
                            isYearOverYear={isYearOverYear}
                            data={data?.chartData}
                        />
                    ) : null}
                    <TrafficByCountryTable
                        isLoading={isLoading}
                        variant={variant}
                        duration={durationData}
                        isYearOverYear={isYearOverYear}
                        data={data?.tableData}
                    />
                </ContentWrapper>
            </WidgetContainer>
        </WidgetContainerWrapper>
    );
};
